import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useDataStore } from "../store/data.store";
import { AccentDot } from "../components/AccentDot";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="w-[18px] h-[18px]">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

export const LoginPage: React.FC = () => {
  const { signIn, isLoading, error } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { membersWithProgress, guRankings } = useDataStore();

  const totalPart = membersWithProgress.length;
  const certified = membersWithProgress.filter((m) => m.status === "certified").length;
  const totalGUs = guRankings.length;

  return (
    <div
      className="relative min-h-screen flex flex-col overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at 30% 20%, rgba(42,26,85,0.6) 0%, transparent 50%)," +
          "radial-gradient(ellipse at 80% 80%, rgba(255,92,78,0.15) 0%, transparent 55%)," +
          "linear-gradient(135deg, #0a0c1f 0%, #0E1138 60%, #1A1F5E 100%)",
      }}
    >
      {/* Decorative italic ampersands */}
      <span
        aria-hidden
        className="absolute pointer-events-none italic font-black font-serif select-none"
        style={{
          left: "-60px",
          top: "50%",
          transform: "translateY(-50%) rotate(-12deg)",
          fontSize: 680,
          color: "rgba(255,92,78,0.07)",
          lineHeight: 1,
        }}
      >
        &amp;
      </span>
      <span
        aria-hidden
        className="absolute pointer-events-none italic font-black font-serif select-none"
        style={{
          right: "-200px",
          bottom: "-180px",
          transform: "rotate(8deg)",
          fontSize: 520,
          color: "rgba(184,212,234,0.04)",
          lineHeight: 1,
        }}
      >
        &amp;
      </span>

      {/* Top-right control — language */}
      <div className="absolute top-5 right-5 z-20">
        <button
          onClick={() => setLanguage(language === "pt-BR" ? "en" : "pt-BR")}
          className="text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/15 text-white/75 hover:text-white hover:bg-white/[0.1] transition-colors"
          title="PT / EN"
        >
          {language === "pt-BR" ? "EN" : "PT"}
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-8 relative z-10">
        <div className="max-w-[520px] w-full text-center">
          {/* Season pulse badge */}
          <div className="inline-flex items-center gap-2.5 mb-8 px-4 py-2 bg-[#FF5C4E]/10 border border-[#FF5C4E]/30 rounded-3xl">
            <span
              className="w-2 h-2 rounded-full bg-[#FF5C4E] animate-pulse"
              style={{ boxShadow: "0 0 10px #FF5C4E" }}
            />
            <span className="text-[11px] tracking-[0.22em] uppercase font-extrabold text-[#FF8C7E]">
              {t.liga.seasonBadge}
            </span>
          </div>

          <h1 className="text-[64px] sm:text-[84px] font-black leading-[0.9] tracking-[-0.04em] text-white mb-3">
            AGENTIC
            <br />
            LEAGUE<AccentDot />
          </h1>
          <p className="text-[15px] text-white/60 max-w-[380px] mx-auto leading-relaxed mb-12">
            {t.login.subtitle}
          </p>

          {/* Login card */}
          <div
            className="relative bg-gradient-to-b from-[rgba(20,23,28,0.95)] to-[rgba(10,12,31,0.95)] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8"
            style={{
              boxShadow: "0 30px 60px rgba(0,0,0,0.4), 0 0 80px rgba(255,92,78,0.08)",
            }}
          >
            <span className="absolute top-0 left-1/2 -translate-x-1/2 w-[60px] h-[2px] bg-[#FF5C4E] rounded" />
            <div className="text-[18px] font-bold text-white mb-6 tracking-tight">
              {t.login.loginPrompt}
            </div>

            {error && (
              <div className="mb-4 rounded-xl bg-red-500/10 border border-red-500/25 px-4 py-3 text-red-300 text-sm text-center">
                {error}
              </div>
            )}

            <button
              onClick={() => signIn()}
              disabled={isLoading}
              className="w-full px-4 py-3.5 bg-white text-[#0E1138] rounded-xl text-sm font-bold flex items-center justify-center gap-3 hover:-translate-y-px transition-transform disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 cursor-pointer"
            >
              <GoogleIcon />
              {isLoading ? t.login.authenticating : t.login.signInButton}
            </button>

            <p className="mt-4 text-[11px] text-white/45 text-center">{t.login.accessNote}</p>
          </div>
        </div>
      </div>

      {/* Bottom stats bar — live data after DataLoader completes */}
      {totalPart > 0 && (
        <div
          className="border-t px-6 py-4 flex justify-center relative z-10"
          style={{
            background: "rgba(10,12,31,0.6)",
            borderColor: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div className="flex gap-6 sm:gap-10 flex-wrap justify-center">
            {[
              { label: t.liga.metrics.participants, value: totalPart.toLocaleString("pt-BR") },
              { label: t.liga.metrics.certified, value: certified.toString() },
              { label: t.liga.metrics.activeGUs, value: totalGUs.toString() },
            ].map(({ label, value }) => (
              <div key={label} className="flex flex-col items-center gap-0.5">
                <span className="font-mono-nums font-bold text-lg sm:text-xl text-white">
                  {value}
                </span>
                <span className="text-[11px] uppercase tracking-[0.12em] text-white/45">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
